import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';

interface HighlightPart {
  text: string;
  isMatch: boolean;
}

@Component({
  selector: 'shc-highlight',
  template: `
    <ng-container *ngFor="let part of parts">
      <mark *ngIf="part.isMatch; else normalText">{{ part.text }}</mark>
      <ng-template #normalText>
        <span>{{ part.text }}</span>
      </ng-template>
    </ng-container>
  `,
  styles: [
    `
      .highlight {
        background-color: yellow;
        padding: 0 2px;
        font-weight: bold;
      }
    `
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule]
})
export class HighlightComponent implements OnChanges {
  @Input() text = '';
  @Input() search = '';

  parts: HighlightPart[] = [];
  private matchRegex: RegExp | null = null;

  ngOnChanges(): void {
    // If text or search is empty, return one part without a match flag.
    if (!this.text || !this.search || this.search.trim() === '') {
      this.parts = [{ text: this.text || '', isMatch: false }];
      this.matchRegex = null;
      return;
    }

    const escapedSearch = this.escapeRegExp(this.search);

    // Precompute the regex for an exact match (case-insensitive)
    this.matchRegex = new RegExp(`^${escapedSearch}$`, 'i');

    // Create a regex to split the text (including the matched term)
    const splitRegex = new RegExp(`(${escapedSearch})`, 'gi');

    // Split the text into parts and map each part to include the isMatch flag.
    this.parts = this.text.split(splitRegex).map(part => ({
      text: part,
      isMatch: this.matchRegex ? this.matchRegex.test(part) : false
    }));
  }

  /**
   * Escapes special regex characters in a string.
   */
  private escapeRegExp(text: string): string {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
}
